import React, { createContext, useContext, useMemo, useState } from "react";
import "./Avatar.css";
import {
  AVATAR_TONES,
  assignAvatarTones,
  getAvatarKey,
  resolveAvatarTone,
} from "../lib/avatarPalette";

// Colored-initials avatar, the round badge from the approved Control Room
// mockup (Team Directory card): two letters on a soft gradient that runs from a
// pastel down into the brand purple.
//
// Which pastel is decided once per workspace (lib/avatarPalette.js): a color
// chosen in the Team directory, otherwise a slot nobody else in the directory
// holds, otherwise (people outside the directory) a hash of the email. The
// assignment reaches every badge through AvatarColorProvider below, so the
// same teammate is the same color in the org chart, the access list, every
// picker and the Account page, and callers never pass a color themselves.
//
// The bottom of the gradient and the ring are theme tokens in Avatar.css, so
// light and dark each get readable initials (dark ink on the pastel half).

export { AVATAR_TONES, getAvatarKey };

// Map of identity key -> tone for the current workspace, or null outside a
// provider (every badge then falls back to the hash).
const AvatarColorContext = createContext(null);

/**
 * Feeds every Avatar below it the workspace's color assignment. Give it the
 * Team directory (teamMembers); it recomputes only when that list changes.
 */
export function AvatarColorProvider({ teamMembers, children }) {
  const assigned = useMemo(() => assignAvatarTones(teamMembers), [teamMembers]);
  return (
    <AvatarColorContext.Provider value={assigned}>
      {children}
    </AvatarColorContext.Provider>
  );
}

const NAME_SUFFIXES = new Set([
  "jr",
  "sr",
  "ii",
  "iii",
  "iv",
  "phd",
  "md",
  "esq",
  "mba",
  "cpa",
]);

function clean(value) {
  return String(value == null ? "" : value).trim();
}

function isSuffix(word) {
  return NAME_SUFFIXES.has(word.toLowerCase().replace(/\./g, ""));
}

// First letter of a word, or its first digit when it has no letters at all.
function firstMark(word) {
  const letter = word.match(/\p{L}/u);
  if (letter) return letter[0];
  const digit = word.match(/\p{N}/u);
  return digit ? digit[0] : "";
}

function initialsFromWords(words) {
  const marks = words.map(firstMark).filter(Boolean);
  if (!marks.length) return "";
  const picked = marks.length > 1 ? [marks[0], marks[marks.length - 1]] : [marks[0]];
  return picked.join("").toLocaleUpperCase();
}

function initialsFromEmail(email) {
  const local = clean(email).split("@")[0].split("+")[0];
  if (!local) return "";
  return initialsFromWords(local.split(/[._\-\s]+/).filter(Boolean));
}

function initialsFromName(name) {
  let text = clean(name)
    // "Sam Okafor (Ops)", "Sam Okafor <sam@x.com>", "[Contractor] Sam"
    .replace(/\([^)]*\)|\[[^\]]*\]|<[^>]*>/g, " ")
    .trim();
  if (!text) return "";
  // "Okafor, Sam" is last-name-first; "Sam Okafor, PhD" is not.
  const parts = text
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part && !part.split(/\s+/).every(isSuffix));
  if (parts.length === 2 && !/\s/.test(parts[0])) {
    text = `${parts[1]} ${parts[0]}`;
  } else {
    text = parts.join(" ");
  }
  const words = text
    .split(/\s+/)
    .filter((word) => word && !isSuffix(word) && firstMark(word));
  return initialsFromWords(words);
}

// "Sam Okafor" -> "SO", "Priya" -> "P", no name + "dana.whitfield@x.com" ->
// "DW". A name that is itself an address (some lists fall back to the email
// as the label) is read as one. Returns "" when there is nothing to go on.
export function getInitials(name, email) {
  const label = clean(name);
  if (label && !label.includes("@")) {
    const fromName = initialsFromName(label);
    if (fromName) return fromName;
  }
  return initialsFromEmail(clean(email) || (label.includes("@") ? label : ""));
}

// The tone this person gets outside any workspace context (the hash), or with
// a directory assignment when one is passed. Kept for callers and tests that
// only need the color, not the badge.
export function getAvatarTone(name, email, assigned = null) {
  return resolveAvatarTone({ name, email, assigned });
}

// A real photo, when a record carries one. Only http(s) URLs are used, so a
// stray value can never become a javascript: or file: source.
export function getPersonPhotoUrl(person) {
  if (!person || typeof person !== "object") return "";
  const candidates = [
    person.photoUrl,
    person.avatarUrl,
    person.picture,
    person.imageUrl,
    person.photo,
  ];
  for (const candidate of candidates) {
    const url = clean(candidate);
    if (/^https?:\/\//i.test(url)) return url;
  }
  return "";
}

function PersonGlyph() {
  return (
    <svg
      className="bdAvatar__glyph"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="8.5" r="4" fill="currentColor" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0z" fill="currentColor" />
    </svg>
  );
}

/**
 * Round person badge.
 *
 * Props:
 * - name, email: who it is. Initials come from the name, falling back to the
 *   email's local part. The color is the workspace's for that email (see
 *   AvatarColorProvider), falling back to a hash of the email or name.
 * - tone: a palette key ("sky") that overrides the workspace color. Only for
 *   previews of a choice not saved yet, such as the Edit teammate dialog.
 * - size: diameter in px (default 32).
 * - src: a photo URL. Shown when it loads; the initials stay underneath and
 *   take over if it fails.
 * - decorative (default true): the name is printed right next to the badge,
 *   so it is hidden from screen readers. Pass false where the badge stands
 *   alone; it then carries role="img", an aria-label and a hover title.
 */
export default function Avatar({
  name = "",
  email = "",
  size = 32,
  src = "",
  decorative = true,
  className = "",
  title,
  tone: toneId = "",
}) {
  const [failedSrc, setFailedSrc] = useState("");
  const assigned = useContext(AvatarColorContext);
  const initials = getInitials(name, email);
  const tone = resolveAvatarTone({ name, email, toneId, assigned });
  const photo = /^https?:\/\//i.test(clean(src)) ? clean(src) : "";
  const showPhoto = !!photo && failedSrc !== photo;
  const label = clean(name) || clean(email) || "Unknown teammate";
  const px = Math.max(12, Number(size) || 32);

  const a11y = decorative
    ? { "aria-hidden": "true" }
    : { role: "img", "aria-label": label, title: title || label };

  return (
    <span
      className={`bdAvatar ${className}`.trim()}
      data-tone={tone.id}
      style={{ "--bd-avatar-size": `${px}px`, "--bd-avatar-top": tone.top }}
      {...a11y}
    >
      {initials ? (
        <span className="bdAvatar__initials">{initials}</span>
      ) : (
        <PersonGlyph />
      )}
      {showPhoto ? (
        <img
          className="bdAvatar__img"
          src={photo}
          alt=""
          draggable="false"
          referrerPolicy="no-referrer"
          onError={() => setFailedSrc(photo)}
        />
      ) : null}
    </span>
  );
}
